import { type Component, createSignal, Show } from 'solid-js'
import { Icon } from '@iconify-icon/solid'

import { formatBytes } from '../../../shared/utils/formatBytes'

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2 MB

interface ImageUploadProps {
  readonly value?: string
  readonly onFileChange: (file: File | null, previewUrl: string | null) => void
  readonly error?: string
}

const ImageUpload: Component<ImageUploadProps> = (props) => {
  const [file, setFile] = createSignal<File | null>(null)
  const [preview, setPreview] = createSignal<string | null>(props.value ?? null)
  const [localError, setLocalError] = createSignal<string | null>(null)

  let inputRef: HTMLInputElement | undefined

  const handleFile = (f: File): void => {
    setLocalError(null)

    if (f.size > MAX_FILE_SIZE) {
      setLocalError('Image must be smaller than 2 MB')
      props.onFileChange(null, null)
      return
    }

    if (!f.type.startsWith('image/')) {
      setLocalError('File must be an image')
      props.onFileChange(null, null)
      return
    }

    const url = URL.createObjectURL(f)
    setFile(f)
    setPreview(url)
    props.onFileChange(f, url)
  }

  const handleSelect = (e: Event): void => {
    const input = e.target as HTMLInputElement
    const selected = input.files?.[0]
    if (selected) handleFile(selected)
    input.value = ''
  }

  const handleDrop = (e: DragEvent): void => {
    e.preventDefault()
    const dropped = e.dataTransfer?.files?.[0]
    if (dropped) handleFile(dropped)
  }

  const remove = (): void => {
    setFile(null)
    setPreview(null)
    setLocalError(null)
    props.onFileChange(null, null)
  }

  const error = () => localError() || props.error

  return (
    <div class="flex flex-col gap-1">
      <input ref={inputRef} type="file" accept="image/*" class="hidden" onChange={handleSelect} />

      <Show
        when={preview()}
        fallback={
          <button
            type="button"
            class="w-full px-3.5 py-2 bg-white rounded-full shadow-sm border border-gray-200 flex items-center justify-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
            onClick={() => inputRef?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
          >
            <Icon icon="heroicons:cloud-arrow-up" class="text-[1rem]" />
            Upload an image
          </button>
        }
      >
        {/* File card */}
        <div class="p-2 bg-white rounded-lg border border-gray-200 flex items-center gap-3 animate-fade-in-right">
          <img src={preview()!} alt="" class="w-14 h-14 rounded-lg object-cover shrink-0" />
          <div class="flex-1 min-w-0 flex flex-col gap-0.5">
            <span class="text-sm font-medium text-gray-900 truncate">{file()?.name ?? 'Image'}</span>
            <Show when={file()}>
              <span class="text-xs text-gray-500">{formatBytes(file()!.size)}</span>
            </Show>
          </div>
          <button
            type="button"
            class="shrink-0 w-7 h-7 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-150"
            onClick={remove}
          >
            <Icon icon="heroicons:x-mark" class="text-[1rem]" />
          </button>
        </div>
      </Show>

      <Show when={error()}>
        <p class="text-xs font-semibold text-red-500">{error()}</p>
      </Show>
    </div>
  )
}

export default ImageUpload
